import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useUserStore } from './useUserStore'

const BASE_URL = process.env.VUE_APP_API_URL || 'http://localhost:3000'

export const usePresenceStore = defineStore('presence', () => {
  const users = ref([]) // [{ uid, email, page, territoryId, lastSeen }]
  let eventSource = null
  let heartbeatTimer = null
  // Último page/territoryId reportado explícitamente: el heartbeat los reenvía
  // para refrescar lastSeen sin pisarlos con null cada 30s.
  let lastPage = null
  let lastTerritoryId = null

  const userStore = useUserStore()

  const othersOnTerritory = computed(() => (territoryId) => {
    if (!territoryId) return []
    return users.value.filter(
      (u) => u.uid !== userStore.uid && u.territoryId === territoryId
    )
  })

  const othersOnPage = computed(() => (page) => {
    if (!page) return []
    return users.value.filter(
      (u) => u.uid !== userStore.uid && u.page === page
    )
  })

  const connect = () => {
    if (!userStore.hasCongregation || !userStore.token) return
    if (eventSource) eventSource.close()
    // Evita acumular un heartbeat nuevo en cada reconexión del SSE (error de red,
    // laptop suspendida, timeout del proxy, redeploy del backend): sin este clear,
    // cada reconexión sumaba otro setInterval que quedaba corriendo para siempre.
    if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null }

    const token = userStore.token
    eventSource = new EventSource(`${BASE_URL}/api/presence/stream?token=${encodeURIComponent(token)}`)

    // El backend usa auth middleware con header — para SSE usamos query param
    // Alternativa: reconectamos con fetch+ReadableStream. Usamos URL con token.
    eventSource.onmessage = (e) => {
      try {
        const event = JSON.parse(e.data)
        handleEvent(event)
      } catch { /* ignorar mensajes malformados */ }
    }

    eventSource.onerror = () => {
      // Reconectar automáticamente después de 5s
      eventSource.close()
      setTimeout(() => connect(), 5000)
    }

    // Heartbeat propio: enviar presencia cada 30s para evitar timeout
    heartbeatTimer = setInterval(() => sendPresence(), 30_000)
  }

  const disconnect = () => {
    if (eventSource) { eventSource.close(); eventSource = null }
    if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null }
    users.value = []
    lastPage = null
    lastTerritoryId = null
    // Notificar al servidor
    fetch(`${BASE_URL}/api/presence`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${userStore.token}`,
      },
    }).catch(() => {})
  }

  // Llamado con argumentos explícitos actualiza qué se está viendo; llamado sin
  // argumentos (heartbeat) reenvía lo último conocido, sólo para refrescar lastSeen.
  const sendPresence = (page, territoryId) => {
    if (page !== undefined) lastPage = page || null
    if (territoryId !== undefined) lastTerritoryId = territoryId || null
    if (!userStore.hasCongregation || !userStore.token) return
    fetch(`${BASE_URL}/api/presence`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userStore.token}`,
      },
      body: JSON.stringify({ page: lastPage, territoryId: lastTerritoryId }),
    }).catch(() => {})
  }

  const handleEvent = (event) => {
    if (event.type === 'snapshot') {
      users.value = event.presence || []
    } else if (event.type === 'update') {
      const idx = users.value.findIndex((u) => u.uid === event.uid)
      const entry = {
        uid: event.uid,
        email: event.email,
        page: event.page,
        territoryId: event.territoryId,
        lastSeen: event.lastSeen,
      }
      if (idx >= 0) {
        users.value[idx] = entry
      } else {
        users.value.push(entry)
      }
    } else if (event.type === 'leave') {
      users.value = users.value.filter((u) => u.uid !== event.uid)
    }
  }

  return {
    users,
    othersOnTerritory,
    othersOnPage,
    connect,
    disconnect,
    sendPresence,
  }
})
